import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { formatActivityType, getCategoryColor } from './utils';
import { Clock, User, FileText, Monitor } from 'lucide-react';
// Detail components
import { InvoiceSentDetail, InvoiceBatchDetail, CreditMemoActivityDetail, StateChangeDetail, ApprovalDetail, ReapprovalRequestedDetail, WorkOrderUpdateDetail, ServiceEventDetail, ServiceStateChangeDetail, DepartmentEventDetail, AssignmentDetail, NoteDetail, FileDetail, ReportDetail, UserAdminDetail, WorkSessionDetail, GenericChangesDetail, AccountAdminDetail, LocationAdminDetail, ServiceCatalogDetail, AdjustmentAppliedActivity, AdjustmentRemovedActivity, AdjustmentRuleConfigActivity, InvoiceRuleChangeDetail, describeDevice, isInvoiceRuleActivityType, } from './details';
// Import shared colors for the container-level styles
import { colors } from './details/detail.styles';
/**
 * Main ActivityDetailView component
 */
export const ActivityDetailView = ({ activity, resolvedRefs, importDetails, importDetailsTotal, loadingDetail, detailError, onClose, onWorkOrderClick, onUserClick, }) => {
    const metadata = activity.metadata || {};
    const activityType = activity.activity_type;
    const category = activity.category;
    // Format timestamp
    const formatTimestamp = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        });
    };
    const registry = [
        // Exact-match entries
        {
            match: (t) => t === 'work_order.credit_created',
            render: () => _jsx(CreditMemoActivityDetail, { activity: activity, metadata: metadata, onWorkOrderClick: onWorkOrderClick }),
        },
        {
            match: (t) => t === 'work_order.invoice_sent',
            render: () => _jsx(InvoiceSentDetail, { activity: activity, metadata: metadata, onUserClick: onUserClick }),
        },
        { match: (t) => t === 'work_order.updated', render: () => _jsx(WorkOrderUpdateDetail, { activity: activity, metadata: metadata }) },
        { match: (t) => t === 'work_order.approved', render: () => _jsx(ApprovalDetail, { activity: activity, metadata: metadata }) },
        {
            match: (t) => t === 'work_order.reapproval_requested',
            render: () => _jsx(ReapprovalRequestedDetail, { activity: activity, metadata: metadata }),
        },
        {
            match: (t) => t === 'work_order_adjustment.applied',
            render: () => _jsx(AdjustmentAppliedActivity, { activity: activity, metadata: metadata }),
        },
        {
            match: (t) => t === 'work_order_adjustment.removed',
            render: () => _jsx(AdjustmentRemovedActivity, { activity: activity, metadata: metadata }),
        },
        // adjustment_rule.created/.updated/.deleted share one component; the
        // action prop comes from the type suffix (unknown suffixes read as
        // "updated" — the least-wrong header).
        {
            match: (t) => t.startsWith('adjustment_rule.'),
            render: () => {
                const suffix = activityType.split('.')[1];
                const action = suffix === 'created' || suffix === 'deleted' ? suffix : 'updated';
                return _jsx(AdjustmentRuleConfigActivity, { activity: activity, metadata: metadata, action: action });
            },
        },
        // Invoice schedules (invoice_rule.*) and delivery lists
        // (invoice_delivery_list.*): who, where, and before → after.
        {
            match: (t) => t.startsWith('invoice_rule.') || t.startsWith('invoice_delivery_list.'),
            render: () => _jsx(InvoiceRuleChangeDetail, { activity: activity, metadata: metadata }),
        },
        // Prefix-match entries — order matters
        {
            match: (t) => t.startsWith('invoice_batch.'),
            render: () => (_jsx(InvoiceBatchDetail, { activity: activity, metadata: metadata, onUserClick: onUserClick, onWorkOrderClick: onWorkOrderClick })),
        },
        // MUST stay above the work_order_service. prefix entry below. That entry
        // is broad enough to swallow service state changes into ServiceEventDetail,
        // which reads service/price fields and ignores previous_state/new_state —
        // the transition would render as an empty "Service Details" box.
        {
            match: (t) => t === 'work_order_service.uncompleted' || t === 'work_order_service.state_changed',
            render: () => _jsx(ServiceStateChangeDetail, { activity: activity, metadata: metadata }),
        },
        { match: (t) => t.startsWith('work_order_service.'), render: () => _jsx(ServiceEventDetail, { activity: activity, metadata: metadata }) },
        {
            match: (t) => t.startsWith('work_order_department') || t === 'work_order.department_changed',
            render: () => _jsx(DepartmentEventDetail, { activity: activity, metadata: metadata }),
        },
        {
            match: (t) => t.includes('assigned') || t.includes('unassigned'),
            render: () => _jsx(AssignmentDetail, { activity: activity, metadata: metadata, onUserClick: onUserClick }),
        },
        { match: (t) => t.includes('note'), render: () => _jsx(NoteDetail, { activity: activity, metadata: metadata }) },
        { match: (t) => t.includes('file') || t.includes('photo'), render: () => _jsx(FileDetail, { activity: activity, metadata: metadata }) },
        { match: (t) => t.startsWith('report.'), render: () => _jsx(ReportDetail, { activity: activity, metadata: metadata }) },
        {
            match: (t) => t.includes('state_changed') || t === 'work_order.completed' || t === 'work_order.reopened',
            render: () => _jsx(StateChangeDetail, { activity: activity, metadata: metadata }),
        },
        {
            match: (t) => t.startsWith('work_session.') || t.startsWith('break_period.'),
            render: () => _jsx(WorkSessionDetail, { activity: activity, metadata: metadata }),
        },
        // Catalog/admin entity events (turn-connect#132 listener additions).
        // user.* must stay below the assigned/unassigned entry above —
        // user.role_assigned matches both, and AssignmentDetail is the
        // intended renderer.
        { match: (t) => t.startsWith('account.'), render: () => _jsx(AccountAdminDetail, { activity: activity, metadata: metadata }) },
        { match: (t) => t.startsWith('location.'), render: () => _jsx(LocationAdminDetail, { activity: activity, metadata: metadata }) },
        { match: (t) => t.startsWith('service.'), render: () => _jsx(ServiceCatalogDetail, { activity: activity, metadata: metadata }) },
        { match: (t) => t.startsWith('user.'), render: () => _jsx(UserAdminDetail, { activity: activity, metadata: metadata }) },
        // Catch-all
        { match: () => true, render: () => _jsx(GenericChangesDetail, { activity: activity, metadata: metadata }) },
    ];
    const renderDetailContent = () => {
        const entry = registry.find((e) => e.match(activityType));
        return entry ? entry.render() : _jsx(GenericChangesDetail, { activity: activity, metadata: metadata });
    };
    return (_jsxs("div", { style: styles.container, children: [_jsx("div", { style: styles.header, children: _jsxs("div", { style: styles.headerContent, children: [_jsxs("div", { style: styles.activityType, children: [_jsx("span", { style: { ...styles.categoryBadge, backgroundColor: getCategoryColor(category || 'workflow') }, children: category?.replace('_', ' ') || 'Activity' }), _jsx("h2", { style: styles.title, children: formatActivityType(activityType) })] }), _jsx("p", { style: styles.description, children: activity.description })] }) }), _jsxs("div", { style: styles.metaSection, children: [_jsxs("div", { style: styles.metaRow, children: [_jsx(Clock, { size: 14, style: { color: colors.text.secondary } }), _jsx("span", { style: styles.metaLabel, children: "When:" }), _jsx("span", { style: styles.metaValue, children: formatTimestamp(activity.created_at) })] }), activity.actor_name && (_jsxs("div", { style: styles.metaRow, children: [_jsx(User, { size: 14, style: { color: colors.text.secondary } }), _jsx("span", { style: styles.metaLabel, children: "By:" }), _jsx("span", { style: styles.metaValue, children: activity.actor_name })] })), (activity.ip_address || activity.user_agent) && !isInvoiceRuleActivityType(activityType) && (_jsxs("div", { style: styles.metaRow, "data-testid": "activity-from", children: [_jsx(Monitor, { size: 14, style: { color: colors.text.secondary } }), _jsx("span", { style: styles.metaLabel, children: "From:" }), _jsx("span", { style: styles.metaValue, children: [describeDevice(activity.user_agent), activity.ip_address].filter(Boolean).join(' · ') })] })), typeof metadata.work_order_display_id === 'string' && metadata.work_order_display_id && (_jsxs("div", { style: styles.metaRow, children: [_jsx(FileText, { size: 14, style: { color: colors.text.secondary } }), _jsx("span", { style: styles.metaLabel, children: "Work Order:" }), _jsx("span", { style: { ...styles.metaValue, ...styles.linkText }, onClick: () => activity.resource_id && onWorkOrderClick?.(activity.resource_id), children: metadata.work_order_display_id })] }))] }), _jsx("div", { style: styles.detailSection, children: renderDetailContent() }), (resolvedRefs && resolvedRefs.length > 0) || loadingDetail || detailError ? (_jsxs("div", { style: styles.relatedSection, children: [_jsx("div", { style: styles.sectionTitle, children: "Related entities" }), detailError && _jsxs("div", { style: styles.error, children: ["Could not load related details: ", detailError] }), loadingDetail && !resolvedRefs && _jsx("div", { style: styles.loadingHint, children: "Loading related entity names\u2026" }), resolvedRefs && resolvedRefs.length > 0 && (_jsx("dl", { style: styles.refList, children: resolvedRefs.map((ref) => (_jsxs("div", { style: styles.refRow, children: [_jsx("dt", { style: styles.refKey, children: formatRefKey(ref.key) }), _jsxs("dd", { style: styles.refValue, children: [renderResolvedRef(ref, onWorkOrderClick, onUserClick), _jsxs("span", { style: styles.refType, children: ["\u00B7 ", formatRefKey(ref.type)] })] })] }, ref.key + '|' + ref.id))) }))] })) : null, importDetails && importDetails.length > 0 && (_jsxs("div", { style: styles.relatedSection, children: [_jsxs("div", { style: styles.sectionTitle, children: ["Import rows", typeof importDetailsTotal === 'number' && importDetailsTotal > importDetails.length && (_jsxs("span", { style: styles.sectionHint, children: [' ', "\u2014 showing first ", importDetails.length, " of ", importDetailsTotal] }))] }), _jsx("div", { style: styles.importTableScroller, children: _jsxs("table", { style: styles.importTable, children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { style: styles.importTh, children: "Row" }), _jsx("th", { style: styles.importTh, children: "Action" }), _jsx("th", { style: styles.importTh, children: "Item" }), _jsx("th", { style: styles.importTh, children: "Note" })] }) }), _jsx("tbody", { children: importDetails.map((d) => (_jsxs("tr", { children: [_jsx("td", { style: styles.importTd, children: d.row_number }), _jsx("td", { style: styles.importTd, children: _jsx("span", { style: { ...styles.actionPill, ...actionPillStyle(d.action) }, children: d.action }) }), _jsx("td", { style: styles.importTd, children: d.summary || '-' }), _jsx("td", { style: { ...styles.importTd, color: d.error_message ? '#FCA5A5' : colors.text.secondary }, children: d.error_message || formatRowChanges(d.after_values) })] }, d.id))) })] }) })] }))] }));
};
// Format an ID key like "resource_id" → "Resource", "work_order_id" → "Work Order".
function formatRefKey(key) {
    return key
        .replace(/_id$/i, '')
        .split(/[._]/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}
function renderResolvedRef(ref, onWorkOrderClick, onUserClick) {
    const handleClick = ref.type === 'work_order' && onWorkOrderClick
        ? () => onWorkOrderClick(ref.id)
        : ref.type === 'user' && onUserClick
            ? () => onUserClick(ref.id)
            : undefined;
    if (handleClick) {
        return (_jsx("span", { style: { color: colors.accent.blue, cursor: 'pointer', textDecoration: 'underline' }, onClick: handleClick, children: ref.display_name }));
    }
    return _jsx("span", { style: { color: colors.text.primary }, children: ref.display_name });
}
function formatRowChanges(after) {
    if (!after)
        return '';
    const friendly = Object.entries(after)
        .filter(([k]) => !k.endsWith('_id'))
        .map(([k, v]) => `${k}: ${v}`)
        .slice(0, 3);
    return friendly.join(', ');
}
function actionPillStyle(action) {
    switch (action) {
        case 'created':
        case 'success':
        case 'would_create':
            return { background: '#0F3D1A', color: '#86EFAC' };
        case 'updated':
        case 'would_update':
            return { background: '#1E3A5F', color: '#93C5FD' };
        case 'skipped':
            return { background: '#374151', color: '#D1D5DB' };
        case 'error':
            return { background: '#3B0F12', color: '#FCA5A5' };
        default:
            return { background: '#374151', color: '#D1D5DB' };
    }
}
// Container-level styles (header, meta, layout)
const styles = {
    container: {
        backgroundColor: colors.background.primary,
        borderRadius: '8px',
        overflow: 'hidden',
    },
    header: {
        padding: '16px',
        backgroundColor: colors.background.secondary,
        borderBottom: `1px solid ${colors.border.default}`,
    },
    headerContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    activityType: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
    },
    categoryBadge: {
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: 600,
        textTransform: 'uppercase',
        color: colors.text.primary,
    },
    title: {
        margin: 0,
        fontSize: '17px',
        fontWeight: 600,
        color: colors.text.primary,
    },
    description: {
        margin: 0,
        fontSize: '13px',
        color: colors.text.secondary,
        lineHeight: 1.5,
    },
    metaSection: {
        padding: '12px 16px',
        backgroundColor: colors.background.tertiary,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    metaRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '12px',
        flexWrap: 'wrap',
    },
    metaLabel: {
        color: colors.text.tertiary,
    },
    metaValue: {
        color: colors.text.primary,
    },
    linkText: {
        color: colors.accent.blue,
        cursor: 'pointer',
        textDecoration: 'underline',
    },
    detailSection: {
        padding: '16px',
    },
    relatedSection: {
        padding: '12px 16px 16px 16px',
        borderTop: `1px solid ${colors.border.default}`,
        backgroundColor: colors.background.secondary,
    },
    sectionTitle: {
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: colors.text.tertiary,
        marginBottom: 8,
    },
    sectionHint: {
        color: colors.text.tertiary,
        fontWeight: 400,
        textTransform: 'none',
        letterSpacing: 0,
    },
    refList: {
        margin: 0,
        display: 'grid',
        gridTemplateColumns: 'minmax(120px, 1fr) 2fr',
        rowGap: 6,
        columnGap: 12,
    },
    refRow: {
        display: 'contents',
    },
    refKey: {
        color: colors.text.tertiary,
        fontSize: 12,
    },
    refValue: {
        margin: 0,
        color: colors.text.primary,
        fontSize: 12,
        display: 'flex',
        gap: 6,
        flexWrap: 'wrap',
        alignItems: 'baseline',
    },
    refType: {
        color: colors.text.tertiary,
        fontSize: 11,
    },
    error: {
        color: '#FCA5A5',
        fontSize: 12,
        marginBottom: 8,
    },
    loadingHint: {
        color: colors.text.tertiary,
        fontSize: 12,
        fontStyle: 'italic',
    },
    importTableScroller: {
        overflowX: 'auto',
        maxHeight: 300,
        overflowY: 'auto',
        border: `1px solid ${colors.border.default}`,
        borderRadius: 6,
    },
    importTable: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: 12,
    },
    importTh: {
        textAlign: 'left',
        padding: '6px 8px',
        background: colors.background.tertiary,
        color: colors.text.tertiary,
        fontWeight: 600,
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        position: 'sticky',
        top: 0,
    },
    importTd: {
        padding: '6px 8px',
        color: colors.text.primary,
        borderTop: `1px solid ${colors.border.default}`,
        verticalAlign: 'top',
    },
    actionPill: {
        display: 'inline-block',
        padding: '2px 6px',
        borderRadius: 3,
        fontSize: 10,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
    },
};
export default ActivityDetailView;
